import { FileText, Mic, StickyNote, type LucideIcon } from "lucide-react";
import type { NavigatorSpec } from "./destinations";

/**
 * The two surfaces that are reachable but are not rail destinations: Settings, which
 * lives at the foot of the rail, and the capture pages behind the add button.
 */

export interface SettingsSection {
  id: string;
  label: string;
  /** What the section contains, per the handoff. Shown in the placeholder. */
  summary: string;
  milestone: string;
  /** Sections that survive into simple mode (UI-16, M11.2). */
  simple?: boolean;
}

/** The nine sections, in the handoff's order. */
export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    id: "setup",
    label: "Setup",
    summary: "What is still needed before the pipeline can run end to end.",
    // UI-13, finished in M3.4. Every row is derived from live state on the Server, so
    // this is the one section that is never out of date and never has to be refreshed
    // by hand.
    milestone: "M3.4",
  },
  {
    id: "mode",
    label: "Mode",
    summary: "Simple or full. The same data either way — simple hides everything except adding, answering and reading.",
    // UI-16, finished in M13.1. M1.2 wrote M11.2 here from the plan as it then stood;
    // corrected rather than left to mislead the next reader, the same as the Chat
    // destination's was in M10.3.
    milestone: "M13.1",
    simple: true,
  },
  {
    id: "account",
    label: "Account",
    summary: "Signed-in identity, your organization and what it does, sign out.",
    milestone: "M3.4",
    simple: true,
  },
  {
    id: "engines",
    label: "Engines & models",
    summary: "Every ML step sits behind an interface. Pick where it runs and which model it uses.",
    // UI-14, finished in M3.3. The milestone stays as the record of when it landed;
    // nothing reads it once a section renders a real page.
    milestone: "M3.3",
  },
  {
    id: "providers",
    label: "Cloud providers",
    summary: "The services a cloud placement calls, and the keys they need.",
    milestone: "M3.3",
  },
  {
    id: "costs",
    label: "Costs",
    // UI-22, M18.2 — over ARCH-13's cloud spend ledger. Organization-wide spend is
    // organization information, so the route behind this section answers only an owner
    // or admin; the page itself says so to anyone else who reaches it.
    summary: "What the cloud cost this organization, by provider, by pipeline step and by recording.",
    milestone: "M18.2",
  },
  {
    id: "worker",
    label: "Worker",
    summary: "The PC that claims jobs, what it is, and how work reaches it.",
    // SEC-3 landed the token half in M2.1; UI-13 completed the rest in M3.4.
    milestone: "M3.4",
  },
  {
    id: "capture",
    label: "Recorder",
    // The PCs that record meetings and calls (CAP-5, M11.2). A generic recorder — it
    // does not know or care which call app is on screen.
    summary: "The PCs that record for you, and where those recordings go.",
    milestone: "M11.2",
    simple: true,
  },
  {
    id: "agents",
    label: "Connected agents",
    // SEC-7. The consent screen promises "you can disconnect it at any time"; this is the
    // section that makes that true, and it is the only revocation and
    // the only visibility there is.
    summary: "The agents you have connected, what each may do, and how to disconnect one.",
    milestone: "M9.3",
  },
  {
    id: "themes",
    label: "Themes",
    summary: "Dark or light appearance.",
    milestone: "M1.2",
    simple: true,
  },
  {
    id: "advanced",
    label: "Advanced",
    summary: "Developer mode, installation info, world view git remote.",
    // ARCH-8's one control, finished in M3.4.
    milestone: "M3.4",
  },
];

export const SETTINGS_BY_ID = new Map(SETTINGS_SECTIONS.map((s) => [s.id, s]));

export const SETTINGS_NAVIGATOR: NavigatorSpec = {
  title: "Settings",
  sections: [
    {
      id: "get-started",
      label: "Get started",
      rows: [{ id: "setup", label: "Setup", to: "/settings/setup" }],
    },
    {
      id: "settings",
      label: "Settings",
      rows: SETTINGS_SECTIONS.filter((s) => s.id !== "setup").map((s) => ({
        id: s.id,
        label: s.label,
        to: `/settings/${s.id}`,
      })),
    },
  ],
};

/**
 * UI-16 — Settings in simple mode: **Mode · Account · Capture & calendar · Themes**, and no
 * "Get started" section at all.
 *
 * Setup is the one that has to go rather than merely move. It is a checklist of what still
 * blocks an end-to-end pipeline run, and simple mode's whole promise is that its reader
 * never has to know there is a pipeline; a rail warning dot pointing at it would be the
 * machinery asking for attention through the one surface built to hide it.
 *
 * The other five keep their addresses and answer with what they are — see `SettingsPage`.
 * This list decides what is *offered*, never what resolves.
 */
export const SIMPLE_SETTINGS_NAVIGATOR: NavigatorSpec = {
  title: "Settings",
  sections: [
    {
      id: "settings",
      label: "Settings",
      rows: SETTINGS_SECTIONS.filter((s) => s.simple).map((s) => ({
        id: s.id,
        label: s.label,
        to: `/settings/${s.id}`,
      })),
    },
  ],
};

/**
 * What one upload route puts on screen. The copy is the handoff's, verbatim; the
 * accepted extensions are the closed list from CAP-2 and must match the Server's —
 * a mismatch shows up as a file the picker offers and the Server then refuses.
 */
export interface UploadSpec {
  /** The `source_materials.kind` this route creates. */
  kind: "recording" | "document";
  /** Page subtitle, from the handoff. */
  subtitle: string;
  dropTitle: string;
  dropBody: string;
  /** The "MP3 · M4A · WAV — up to 4 GB, one file at a time." line. */
  formatNote: string;
  /** How a refusal names what *is* accepted. Worded exactly as the Server words it,
   * so a file refused here and a file refused there read the same. */
  acceptedSentence: string;
  extensions: string[];
  /** What the primary button says once a file is staged. */
  submitLabel: string;
  /** "Recorded on" reads wrong for a contract, and vice versa. */
  dateLabel: string;
  /** Bytes the Server will accept, so an obviously oversized file is caught here. */
  maxBytes: number;
}

export interface CaptureRoute {
  id: string;
  path: string;
  label: string;
  icon: LucideIcon;
  summary: string;
  milestone: string;
  /** Present on the two routes that are built; absent on the one that is not. */
  upload?: UploadSpec;
}

/**
 * Exactly three ways to add a source, and no in-app recording control — CAP-1 is
 * explicit that recording from the app was tried and is not used.
 */
export const CAPTURE_ROUTES: CaptureRoute[] = [
  {
    id: "audio",
    path: "/add/audio",
    label: "Upload audio",
    icon: Mic,
    summary: "A meeting, call or voice memo from your recorder. mp3, m4a or wav.",
    milestone: "M1.3",
    upload: {
      kind: "recording",
      subtitle: "Drop a recording in and the worker takes it from there.",
      dropTitle: "Drop audio here",
      dropBody:
        "Files are uploaded to your own storage, then transcribed and diarized locally by the worker.",
      formatNote: "MP3 · M4A · WAV — up to 4 GB, one file at a time.",
      acceptedSentence: "Audio is mp3, m4a or wav.",
      extensions: [".mp3", ".m4a", ".wav"],
      submitLabel: "Add to pipeline",
      dateLabel: "Recorded on",
      maxBytes: 4 * 1024 * 1024 * 1024,
    },
  },
  {
    id: "document",
    path: "/add/document",
    label: "Upload a document",
    icon: FileText,
    summary: "Markdown, Word, Excel or plain text.",
    milestone: "M1.3",
    upload: {
      kind: "document",
      subtitle: "Contracts, wiki pages, minutes — anything the assistant should be able to cite.",
      dropTitle: "Drop a document here",
      dropBody: "Stored exactly as it arrived, and cited back to you whenever it answers something.",
      formatNote: "MD · DOCX · XLSX · TXT — up to 100 MB, one file at a time.",
      acceptedSentence: "Documents are md, docx, xlsx or txt.",
      extensions: [".md", ".docx", ".xlsx", ".txt"],
      submitLabel: "Index document",
      dateLabel: "Document date",
      maxBytes: 100 * 1024 * 1024,
    },
  },
  {
    id: "note",
    path: "/add/note",
    label: "Write a note",
    icon: StickyNote,
    summary: "A quick thought or fact, kept like any other source.",
    milestone: "M5.1",
  },
];

export const CAPTURE_BY_ID = new Map(CAPTURE_ROUTES.map((c) => [c.id, c]));

export const CAPTURE_NAVIGATOR: NavigatorSpec = {
  title: "Add a source",
  sections: [
    {
      id: "ways",
      label: "Ways to add",
      rows: CAPTURE_ROUTES.map((c) => ({ id: c.id, label: c.label, to: c.path })),
    },
    { id: "recent", label: "Recently added", rows: [] },
  ],
};
